import React, { useEffect, useState } from 'react';import { TrendingUp, Sparkles, AlertCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import useAiPrefs from '../hooks/useAiPrefs';

/**
 * AI Cash-Flow Forecast widget — small Dashboard card that surfaces
 * upcoming 30/60/90-day inflows plus a 2-line AI commentary so the
 * owner sees both the numbers and what to do about them. Lazy-loads
 * (only fetches when actually rendered, which is when the user is on
 * the dashboard).
 *
 * Gated to plans with the `ai` feature; renders nothing for Starter/Core.
 */
export default function AiCashflowCard() {
  const { enabled, loading } = useAiPrefs();
  const [data, setData] = useState(null);

  // Loading state is derived: `data === null` means we haven't received
  // a response yet (still fetching). Once data arrives OR plan check
  // fails, we set `data` so the busy state ends. This avoids setting
  // state inside the effect (which lint flags) while still showing the
  // spinner during the request.
  const aiAllowed = !loading && enabled('ai_cashflow_card');
  useEffect(() => {
    if (!aiAllowed) return undefined;
    let cancelled = false;
    api.get('/ai/cashflow-forecast')
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ error: true }); });
    return () => { cancelled = true; };
  }, [aiAllowed]);

  if (loading || !enabled('ai_cashflow_card')) return null;
  const busy = data === null;

  return (
    <div data-testid="ai-cashflow-card" className="relative bg-gradient-to-br from-[#1B2838] to-[#1f2f42] border border-[#B98A2C]/15 rounded-xl p-4 sm:p-5 overflow-hidden">
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#B98A2C]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#B98A2C] to-[#CBA33B] flex items-center justify-center">
              <Sparkles size={13} className="text-[#121A25]" strokeWidth={2.6} />
            </div>
            <div>
              <p className="text-[12px] font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>AI Cash-Flow Forecast</p>
              <p className="text-[9px] text-[#758A9F]">Next 30 / 60 / 90 days</p>
            </div>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-[#CBA33B] font-bold">Plus+</span>
        </div>

        {busy ? (
          <div className="flex items-center gap-2 text-[#758A9F] text-[11px] py-4">
            <Loader2 size={12} className="animate-spin" /> Calculating…
          </div>
        ) : !data || data.error || !data.in_30 ? (
          <p className="text-[11px] text-[#758A9F] py-4">No data yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Bucket label="30 days" total={data.in_30.total} count={data.in_30.count} accent="text-emerald-400" />
              <Bucket label="60 days" total={data.in_60.total} count={data.in_60.count} accent="text-[#CBA33B]" />
              <Bucket label="90 days" total={data.in_90.total} count={data.in_90.count} accent="text-blue-400" />
            </div>
            {data.overdue.count > 0 && (
              <div className="flex items-center gap-2 px-2 py-1.5 mb-3 rounded-md bg-red-500/10 border border-red-500/20">
                <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
                <p className="text-[11px] text-red-300">
                  <span className="font-bold">${data.overdue.total.toFixed(2)}</span> overdue across {data.overdue.count} invoice{data.overdue.count !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            {data.summary && (
              <div className="flex items-start gap-2 text-[11px] text-[#D6E0EC] leading-relaxed">
                <TrendingUp size={12} className="text-[#CBA33B] flex-shrink-0 mt-0.5" />
                <p>{data.summary}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Bucket({ label, total, count, accent }) {
  return (
    <div className="bg-white/[0.03] rounded-md px-2 py-2">
      <p className="text-[8px] uppercase tracking-wider text-[#758A9F] font-bold">{label}</p>
      <p className={`text-[14px] font-bold tabular-nums ${accent} mt-0.5`} style={{ fontFamily: "'Outfit', sans-serif" }}>${total.toFixed(0)}</p>
      <p className="text-[8px] text-[#758A9F]">{count} invoice{count !== 1 ? 's' : ''}</p>
    </div>
  );
}
